// Derechos ARCO del titular (LOPDP): Acceso/Portabilidad (exportar datos),
// Eliminación (anonimizar/borrar) y registro de solicitudes con su trazabilidad.
// Panel reutilizable en la ficha del paciente (staff) y en el portal (titular).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, ESTADO_ARCO, Modal, SolicitudArco, TIPO_ARCO, fieldCls, medRpc } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const fmt = (s: string | null) => (s ? new Date(s).toLocaleDateString(LOCALE, { dateStyle: 'medium' }) : '—');

// Descarga un objeto JSON como archivo
function descargarJSON(data: unknown, nombre: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export function PanelArcoPaciente({ pacienteId, portal = false, nombre }: { pacienteId?: string; portal?: boolean; nombre?: string }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const key = ['med-arco', pacienteId ?? 'self'];
  const { data: solicitudes = [] } = useQuery<SolicitudArco[]>({
    queryKey: key, queryFn: () => medRpc<SolicitudArco[]>('med_solicitudes_arco', pacienteId ? { _paciente: pacienteId } : {}),
  });
  const [eliminar, setEliminar] = useState(false);

  const exportar = useMutation({
    mutationFn: () => medRpc<Record<string, unknown>>('med_exportar_datos_paciente', pacienteId ? { _paciente: pacienteId } : {}),
    onSuccess: (data) => {
      const stamp = fechaLocal();
      descargarJSON(data, `datos-personales-${(nombre ?? 'paciente').replace(/\s+/g, '_').toLowerCase()}-${stamp}.json`);
      toast.success('Datos exportados');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Portal: el titular SOLICITA la eliminación (la ejecuta el staff por la retención legal)
  const solicitarElim = useMutation({
    mutationFn: () => medRpc('med_crear_solicitud_arco', { _data: { tipo: 'eliminacion', descripcion: 'Solicito la eliminación de mis datos personales.' } }),
    onSuccess: () => { toast.success('Solicitud de eliminación enviada'); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <section className="space-y-2">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" /> Mis datos personales (LOPDP)</h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" isLoading={exportar.isPending} onClick={() => exportar.mutate()}>
            <Download className="w-4 h-4 mr-1" /> {portal ? 'Descargar mis datos' : 'Exportar datos'}
          </Button>
          {portal ? (
            <Button size="sm" variant="outline" className="text-red-600" onClick={async () => { if (!(await dlg.confirmar('¿Enviar una solicitud para eliminar tus datos personales?', { detalle: 'El personal la atenderá conforme a la ley.', peligro: true }))) return; solicitarElim.mutate(); }}>
              <Trash2 className="w-4 h-4 mr-1" /> Solicitar eliminación
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => setEliminar(true)}>
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar / anonimizar
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Acceso y portabilidad (Art. 13 y 17): exporta todos los datos en formato JSON. La eliminación (Art. 15)
        anonimiza la identidad conservando lo exigido por la normativa de salud.
      </p>

      {solicitudes.length > 0 && (
        <ul className="text-sm divide-y border rounded-md">
          {solicitudes.map((s) => {
            const e = ESTADO_ARCO[s.estado] ?? ESTADO_ARCO.pendiente;
            return (
              <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <div>
                  <span className="font-medium">{TIPO_ARCO[s.tipo] ?? s.tipo}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{fmt(s.created_at)} · límite {fmt(s.fecha_limite)}{s.descripcion ? ` · ${s.descripcion}` : ''}</span>
                  {s.respuesta && <div className="text-xs italic text-muted-foreground">{s.respuesta}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span>
                  {!portal && s.estado !== 'atendida' && s.estado !== 'rechazada' && <ResolverBtn solicitud={s} onDone={() => void qc.invalidateQueries({ queryKey: key })} />}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {eliminar && pacienteId && (
        <EliminarModal pacienteId={pacienteId} onClose={() => setEliminar(false)} onDone={() => { setEliminar(false); void qc.invalidateQueries({ queryKey: key }); }} />
      )}
    </section>
  );
}

function ResolverBtn({ solicitud, onDone }: { solicitud: SolicitudArco; onDone: () => void }) {
  const [abrir, setAbrir] = useState(false);
  const [estado, setEstado] = useState('atendida');
  const [respuesta, setRespuesta] = useState('');
  const resolver = useMutation({
    mutationFn: () => medRpc('med_resolver_solicitud_arco', {
      _id: solicitud.id, _estado: estado, _respuesta: respuesta || null,
      _adjuntar_export: estado === 'atendida' && (solicitud.tipo === 'acceso' || solicitud.tipo === 'portabilidad'),
    }),
    onSuccess: () => { toast.success('Solicitud resuelta'); setAbrir(false); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setAbrir(true)}>Resolver</Button>
      {abrir && (
        <Modal title={`Resolver solicitud · ${TIPO_ARCO[solicitud.tipo] ?? solicitud.tipo}`} onClose={() => setAbrir(false)}>
          <div className="space-y-3">
            <Campo label="Resultado">
              <select className={fieldCls} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="atendida">Atendida</option>
                <option value="en_proceso">En proceso</option>
                <option value="rechazada">Rechazada</option>
              </select>
            </Campo>
            <Campo label="Respuesta al titular"><AutoTextarea value={respuesta} onChange={(e) => setRespuesta(e.target.value)} placeholder="Detalle de la atención (motivo si se rechaza, etc.)" /></Campo>
            {(solicitud.tipo === 'acceso' || solicitud.tipo === 'portabilidad') && estado === 'atendida' && (
              <p className="text-xs text-muted-foreground">Se adjuntará una copia de los datos entregados como constancia.</p>
            )}
            {solicitud.tipo === 'eliminacion' && (
              <p className="text-xs text-pendiente">La eliminación se ejecuta aparte con el botón “Eliminar / anonimizar”. Aquí solo registras la respuesta.</p>
            )}
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAbrir(false)}>Cancelar</Button><Button isLoading={resolver.isPending} onClick={() => resolver.mutate()}>Guardar</Button></div>
          </div>
        </Modal>
      )}
    </>
  );
}

function EliminarModal({ pacienteId, onClose, onDone }: { pacienteId: string; onClose: () => void; onDone: () => void }) {
  const [modo, setModo] = useState<'anonimizar' | 'eliminar'>('anonimizar');
  const [motivo, setMotivo] = useState('');
  const [confirmo, setConfirmo] = useState(false);
  const ejecutar = useMutation({
    mutationFn: () => medRpc('med_eliminar_datos_paciente', { _paciente: pacienteId, _modo: modo, _motivo: motivo || null }),
    onSuccess: () => { toast.success('Datos del paciente eliminados/anonimizados'); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title="Eliminar datos del paciente (Art. 15 LOPDP)" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">Esta acción es <b>irreversible</b>. Elige cómo proceder según la obligación de conservación de la historia clínica.</p>
        <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={modo === 'anonimizar'} onChange={() => setModo('anonimizar')} />
          <span><b>Anonimizar</b> (recomendado): borra nombre, contacto, familiares y acceso al portal; conserva los registros clínicos ya disociados de la identidad.</span></label>
        <label className="flex items-start gap-2"><input type="radio" className="mt-1" checked={modo === 'eliminar'} onChange={() => setModo('eliminar')} />
          <span><b>Eliminación total</b>: lo anterior + borra la identificación y da de baja todos los registros clínicos. Úsalo solo si no aplica un plazo legal de conservación.</span></label>
        <Campo label="Motivo (queda en auditoría)"><input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: solicitud de eliminación del titular" /></Campo>
        <label className="flex items-center gap-2"><input type="checkbox" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} /> Confirmo que deseo {modo === 'eliminar' ? 'eliminar' : 'anonimizar'} estos datos.</label>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-red-600 hover:bg-red-700" isLoading={ejecutar.isPending} disabled={!confirmo} onClick={() => ejecutar.mutate()}>Ejecutar</Button>
        </div>
      </div>
    </Modal>
  );
}
